import { spread } from 'axios';
import './styles/Collage.scss';
import { useState, useRef, useEffect, ReactElement } from 'react';

//classes
class Dimension {
    public width: number = 0;
    public height: number = 0;
    public w_h_ratio: number = 0;
    constructor(width: number, height: number, ratio: number) {
        this.width = width;
        this.height = height;
        this.w_h_ratio = ratio;
    }
}

interface Props {
    mode: string,
    spreadCount: number,
    minSpreadRatio: number,
    maxSpreadRatio: number,
    frameWidth: number, //in ratio respect to (mode == row -> height, mode == column -> width)
    frameRadius: number,
    images: string[]
}

const Collage: React.FC<Props> = ({
    mode,
    spreadCount,
    minSpreadRatio,
    maxSpreadRatio,
    frameWidth, frameRadius,
    images
}) => {
    //states - vars
    //states - collage draw
    const [spreads, SetSpreads] = useState<any[]>([] as any[]); //inside spreads holds array of images

    //refs - vars & objs
    const collageRef = useRef<any>(null);
    const collageDimensionRef = useRef<Dimension>(new Dimension(0, 0, 0));
    const imagesDimensionRef = useRef<Dimension[]>([] as Dimension[]);
    const imagesLoadedRef = useRef<boolean[]>([] as boolean[]);
    const spreadCountRef = useRef<number>(spreadCount);
    const modeRef = useRef<string>(mode);

    //refs - collage generation use
    const spreadRatioRef = useRef<number[]>([]); //will changed randomly when ruler matches all rows/columns end points
    // const rulerPosRef = useRef<number>(0); //a virtual ruler to measure if all rows/columns has met the same point
    const endPointsRef = useRef<number[]>([] as number[]);
    const spreadsRef = useRef<any[]>(spreads);
    const xFrameWidthRef = useRef<number>(0);
    const xFrameRadiusRef = useRef<number>(0);
    const shouldDrawImageIndexRef = useRef<number>(0);

    //refs - interval
    const drawCollageIntervalRef = useRef<any>(null);

    //watchers
    //initialize
    useEffect(() => {
        return (() => {
            if (drawCollageIntervalRef.current != null) {
                clearInterval(drawCollageIntervalRef.current);
                drawCollageIntervalRef.current = null;
            }
        });
    }, []);

    //watch for mode or other props update
    useEffect(() => {
        //preps
        const collageWidth = collageRef.current.getBoundingClientRect().width;
        const collageHeight = collageRef.current.getBoundingClientRect().height;
        const collageRatio = collageWidth / collageHeight;

        collageDimensionRef.current = new Dimension(collageWidth, collageHeight, collageRatio);

        if (modeRef.current == 'column') {
            xFrameWidthRef.current = (frameWidth * collageWidth);
            xFrameRadiusRef.current = (frameRadius * collageWidth);

        } else {
            xFrameWidthRef.current = (frameWidth * collageHeight);
            xFrameRadiusRef.current = (frameRadius * collageHeight);
        }

        //image dimensions initialize
        LoadImages();

        //initialize endpoints and spreads
        spreadsRef.current = [];
        endPointsRef.current = [];
        for (let i = 0; i < spreadCount; i++) {
            endPointsRef.current.push(0);
            spreadsRef.current.push([]);
        }
        SetSpreads(spreadsRef.current);

        //start drawing collage
        if (drawCollageIntervalRef.current != null) {
            clearInterval(drawCollageIntervalRef.current);
            drawCollageIntervalRef.current = null;
        }

        //reset draw index
        shouldDrawImageIndexRef.current = 0;
        drawCollageIntervalRef.current = setInterval(() => {
            //checks and draw collage every second
            //if image loaded - draw them in sequence

            //reInitialize spreadRatios
            if (IsAllEndPointsAligned()) {
                spreadRatioRef.current = GenerateRandomSpreadRatios();
                console.log('checkSpreadRatio: ' + spreadRatioRef.current);
            }

            // let shouldDrawImgCount = 0; //starts drawing from index = 0

            if (imagesLoadedRef.current[shouldDrawImageIndexRef.current]) {
                const spreadIndex = GetSpreadIndexFromImageIndex(shouldDrawImageIndexRef.current);

                const scaledDimension = GetScaledImageDimension(imagesDimensionRef.current[shouldDrawImageIndexRef.current], spreadIndex);
                console.log('checkScaledDimension: ' + JSON.stringify(scaledDimension));

                spreadsRef.current[spreadIndex].push(
                    <img className='Collage-spread-img' src={images[shouldDrawImageIndexRef.current]} style={{ width: scaledDimension.width, height: scaledDimension.height, margin: `${xFrameWidthRef.current}px`, borderRadius: `${xFrameRadiusRef.current}px` }} />
                );

                //update to stateSpreads & increase endPoints
                SetSpreads([...spreadsRef.current]);

                if (modeRef.current == 'columne')
                    endPointsRef.current[spreadIndex] += (xFrameWidthRef.current * 2) + scaledDimension.height;
                else
                    endPointsRef.current[spreadIndex] += (xFrameWidthRef.current * 2) + scaledDimension.width;

                //finalize
                if (shouldDrawImageIndexRef.current < images.length - 1)
                    shouldDrawImageIndexRef.current += 1;
                else {
                    clearInterval(drawCollageIntervalRef.current);
                    drawCollageIntervalRef.current = null;
                }
            }

            console.log('checkShouldDrawImageIndex: ' + shouldDrawImageIndexRef.current);
        }, 100);
    }, [
        mode,
        spreadCount, minSpreadRatio, maxSpreadRatio,
        frameWidth, frameRadius,
        images
    ]);

    //watch for spreadCount update spreadCountRef
    useEffect(() => {
        spreadCountRef.current = spreadCount;
    }, [spreadCount]);

    //watch for mode update modeRef
    useEffect(() => {
        modeRef.current = mode;
    }, [mode]);

    //exe functions
    const LoadImages = () => {
        images.map((imageSrc: string, i: number) => {
            imagesDimensionRef.current.push(new Dimension(0, 0, 0));
            imagesLoadedRef.current.push(false);

            const img = new Image();
            img.src = imageSrc;
            img.onload = () => {
                imagesDimensionRef.current[i] = new Dimension(img.naturalWidth, img.naturalHeight, img.naturalWidth / img.naturalHeight);
                imagesLoadedRef.current[i] = true;
            };
        });
    };
    const GetSpreadIndexFromImageIndex = (itemIndex: number) => {
        //try to get spread index count itemCount
        let spreadIndex = itemIndex / spreadCountRef.current;
        if (spreadIndex >= 1)
            spreadIndex -= Math.floor(spreadIndex);

        const spreadIndexRatio = 1 / spreadCountRef.current;
        spreadIndex = Math.round(spreadIndex / spreadIndexRatio);

        console.log('checkSpreadIndex: ' + spreadIndex);

        return spreadIndex;
    }
    const GenerateRandomSpreadRatios = () => {
        const res = [] as number[];
        const seedRatio = minSpreadRatio + (Math.random() * (maxSpreadRatio - minSpreadRatio));
        let ratioAllocated = 0;
        for (let i = 0; i < spreadCountRef.current; i++) {
            if (i != spreadCountRef.current - 1) {
                const _seedRatio = (1 - ratioAllocated) * seedRatio;
                res.push(_seedRatio);
                ratioAllocated += _seedRatio;
            } else {
                res.push(1 - ratioAllocated);
            }
        }
        return res;
    }
    const GetScaledImageDimension = (dimension: Dimension, spreadIndex: number) => {
        let newWidth = 0; let newHeight = 0;
        if (modeRef.current == 'column') {
            newWidth = (collageDimensionRef.current.width * spreadRatioRef.current[spreadIndex]) - (xFrameWidthRef.current * 2);
            newHeight = newWidth / dimension.w_h_ratio;
        } else {
            newHeight = (collageDimensionRef.current.height * spreadRatioRef.current[spreadIndex]) - (xFrameWidthRef.current * 2);
            newWidth = newHeight * dimension.w_h_ratio;
        }
        return new Dimension(newWidth, newHeight, dimension.w_h_ratio);
    }

    //check function
    const IsAllEndPointsAligned = () => {
        let res = true;
        let prevEndPoint = endPointsRef.current[0];
        endPointsRef.current.map((endPoint: number) => {
            if (endPoint.toFixed(2) != prevEndPoint.toFixed(2)) {
                res = false;
            }
            prevEndPoint = endPoint;
        });
        return res;
    }

    return (
        <div className={'Collage ' + mode} ref={collageRef}>
            {
                spreads.map((spread: any) => {
                    return (
                        <div className='Collage-spread'>
                            {
                                (spread as ReactElement<HTMLImageElement>[]).map((image: ReactElement<HTMLImageElement>) => {
                                    return image;
                                })
                            }
                        </div>
                    )
                })
            }
        </div>
    );
}
export default Collage;